// client/src/components/pages/LexiStar/NotificationRules/NotificationRules.tsx (converted from .jsx, ST-00282)
//
// LexiStar notification rules, groups & templates (ST-00188 M3, T8-S01;
// LS-09 §5.16; FR-NTF-001…009, FR-NTF-012). Route
// /settings/modules/monitoring/lexistar-notifications, page permission
// lexistar.notifications.view; changes need lexistar.notifications.manage.
// Data: server/modules/lexistar/notifications (m3-contracts §7.2).
//
// Tabs: Rules (list + rule builder) · Groups · Templates (overrides with a
// live preview) · Budgets (per channel, spend this month) · Settings (tenant
// SLAs, review threshold, default driver language).
//
// ST-00282: a rule the server marks `locked` (the seeded no-go rule: a no-go zone always alerts)
// shows "Always on" with a lock and the server's reason instead of a Pause button. The server
// refuses any change that would stop it alerting on every no-go entry (422 NOGO_RULE_LOCKED);
// everything else still changes in the rule builder.

import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaBell, FaLock, FaPlus } from 'react-icons/fa';
import SegmentedControlJs from '../../../common/SegmentedControl';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import {
  PageHeader,
  ErrorBanner,
  NoticeBanner,
  NoPermissionPage,
  LoadingState,
  EmptyState,
  cardClasses,
} from '../StatusBoard/atoms';
import RuleBuilder, { type BuilderGroup, type BuilderRule } from './RuleBuilder';
import RecipientGroupsJs from './RecipientGroups';
import TemplatesJs from './Templates';
import BudgetsJs from './Budgets';
import TenantSettings from './TenantSettings';
import {
  listRules as listRulesJs,
  listGroups as listGroupsJs,
  listTemplates as listTemplatesJs,
  updateRule as updateRuleJs,
  pickRows as pickRowsJs,
} from './notificationsApi';
import { describeRule, channelName } from './ruleLogic';

type Row = Record<string, unknown>;

/** A recipient group as the Groups tab and describeRule read it. */
type GroupRow = BuilderGroup;

/** A rule row, with the server's `locked` (ST-00282). */
type RuleRow = BuilderRule;

/** An axios error, as far as this page reads it. */
interface ApiError {
  response?: { status?: number; data?: { error?: string } };
}

// Typed views of the JavaScript modules this page uses.
const SegmentedControl = SegmentedControlJs as unknown as ComponentType<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}>;
const RecipientGroups = RecipientGroupsJs as unknown as ComponentType<{
  groups: GroupRow[];
  users: Row[];
  canManage: boolean;
  onChanged: () => void;
}>;
const Templates = TemplatesJs as unknown as ComponentType<{
  templates: Row[];
  canManage: boolean;
  onChanged: () => void;
}>;
const Budgets = BudgetsJs as unknown as ComponentType<{ canManage: boolean }>;
const listRules = listRulesJs as unknown as () => Promise<unknown>;
const listGroups = listGroupsJs as unknown as () => Promise<unknown>;
const listTemplates = listTemplatesJs as unknown as () => Promise<unknown>;
const updateRule = updateRuleJs as unknown as (id: string, data: Row) => Promise<unknown>;
const pickRows = pickRowsJs as unknown as <T = Row>(res: unknown, ...keys: string[]) => T[];

const TABS = [
  { value: 'rules', label: 'Rules' },
  { value: 'groups', label: 'Groups' },
  { value: 'templates', label: 'Templates' },
  { value: 'budgets', label: 'Budgets' },
  { value: 'settings', label: 'Settings' },
];

const NotificationRules = () => {
  const { hasPermission } = useAuth();
  const canView =
    hasPermission('lexistar.notifications.view') || hasPermission('lexistar.notifications.manage');
  const canManage = hasPermission('lexistar.notifications.manage');
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab = tabParam && TABS.some((t) => t.value === tabParam) ? tabParam : 'rules';

  const [rules, setRules] = useState<RuleRow[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [templates, setTemplates] = useState<Row[]>([]);
  const [users, setUsers] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // undefined = closed; null = a new rule; a row = editing it.
  const [editing, setEditing] = useState<RuleRow | null | undefined>(undefined);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    const [r, g, t] = await Promise.allSettled([listRules(), listGroups(), listTemplates()]);
    if (r.status === 'fulfilled') setRules(pickRows<RuleRow>(r.value, 'rules', 'notification_rules'));
    if (g.status === 'fulfilled') setGroups(pickRows<GroupRow>(g.value, 'groups', 'recipient_groups'));
    if (t.status === 'fulfilled') setTemplates(pickRows(t.value, 'templates', 'message_templates'));
    const failed = [r, g, t].find((x): x is PromiseRejectedResult => x.status === 'rejected');
    const reason = failed?.reason as ApiError | undefined;
    setError(
      failed
        ? reason?.response?.status === 503
          ? 'Notifications are not installed on this server yet.'
          : reason?.response?.data?.error || 'Some notification settings failed to load.'
        : null
    );
    setLoading(false);
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  // Users for "a user" recipients — only when the caller may list users.
  useEffect(() => {
    if (!canManage) return;
    api
      .get('/users', { params: { status: 'active' } })
      .then((res) => setUsers(Array.isArray(res.data) ? (res.data as Row[]) : pickRows(res.data, 'users')))
      .catch(() => setUsers([]));
  }, [canManage]);

  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const toggleActive = async (rule: RuleRow) => {
    setBusy(rule.id);
    try {
      await updateRule(rule.id, {
        active: !rule.active,
        ...(rule.version ? { version: rule.version } : {}),
      });
      setNotice(`${rule.name} ${rule.active ? 'paused' : 'switched on'}.`);
      load();
    } catch (err) {
      setError((err as ApiError).response?.data?.error || 'The rule was not changed.');
    } finally {
      setBusy(null);
    }
  };

  const header = (
    <PageHeader
      icon={FaBell}
      title="Notification Rules"
      subtitle="Who hears about what, on which channel, and when to escalate."
      toolbar={
        canView ? (
          <SegmentedControl
            options={TABS}
            value={tab}
            onChange={(v) => setParams(v === 'rules' ? {} : { tab: v }, { replace: true })}
            ariaLabel="Notification settings"
          />
        ) : null
      }
      actions={
        canManage && tab === 'rules' ? (
          <button
            type="button"
            onClick={() => setEditing(null)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
          >
            <FaPlus size={11} /> New rule
          </button>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view notification rules."
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

        {tab === 'rules' &&
          (loading && rules.length === 0 ? (
            <LoadingState label="Loading rules..." />
          ) : rules.length === 0 ? (
            <div className={cardClasses}>
              <EmptyState icon={FaBell} title="No notification rules">
                The standard set is seeded when LexiStar is installed.{' '}
                {canManage ? 'Create a rule to add your own.' : ''}
              </EmptyState>
            </div>
          ) : (
            <section className={cardClasses} aria-label="Rules">
              <ul className="divide-y divide-slate-100">
                {rules.map((r) => (
                  <li
                    key={r.id}
                    className={`px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 ${r.active ? '' : 'opacity-60'}`}
                  >
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900">{r.name}</span>
                        {r.locked ? (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800"
                            title={r.locked.reason}
                          >
                            <FaLock size={8} aria-hidden="true" />
                            Always on
                          </span>
                        ) : (
                          <span
                            className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${r.active ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}
                          >
                            {r.active ? 'On' : 'Paused'}
                          </span>
                        )}
                        {(r.channels || []).map((c) => (
                          <span
                            key={c}
                            className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] text-slate-700"
                          >
                            {channelName(c)}
                          </span>
                        ))}
                        {(r.escalation || []).length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-[11px] font-semibold text-amber-800">
                            escalates
                          </span>
                        )}
                      </span>
                      <span className="block mt-1 text-sm text-slate-600">
                        {describeRule({ ...r, template_key: r.template_key ?? null }, { groupsById })}
                      </span>
                      {r.locked && (
                        <span className="block mt-1 text-xs text-slate-500" data-testid="rule-locked-reason">
                          {r.locked.reason}
                        </span>
                      )}
                    </button>
                    {canManage && !r.locked && (
                      <button
                        type="button"
                        onClick={() => toggleActive(r)}
                        disabled={busy === r.id}
                        className="self-start md:self-center px-3 py-1.5 text-xs font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                      >
                        {r.active ? 'Pause' : 'Switch on'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}

        {tab === 'groups' && (
          <RecipientGroups groups={groups} users={users} canManage={canManage} onChanged={load} />
        )}
        {tab === 'templates' && (
          <Templates templates={templates} canManage={canManage} onChanged={load} />
        )}
        {tab === 'budgets' && <Budgets canManage={canManage} />}
        {tab === 'settings' && <TenantSettings canManage={canManage} />}
      </div>

      <RuleBuilder
        isOpen={editing !== undefined}
        rule={editing}
        templates={templates}
        groups={groups}
        users={users}
        canManage={canManage}
        onClose={() => setEditing(undefined)}
        onSaved={(saved) => {
          setNotice(`${saved?.name || 'The rule'} saved.`);
          load();
        }}
      />
    </div>
  );
};

export default NotificationRules;
